import { supabase } from '../supabase';
import { Database } from '../../types/database';

// Type aliases for better readability
type Vendor = Database['public']['Tables']['vendors']['Row'];
type VendorInsert = Database['public']['Tables']['vendors']['Insert'];
type VendorUpdate = Database['public']['Tables']['vendors']['Update'];
type VendorOnboardingProgress = Database['public']['Tables']['vendor_onboarding_progress']['Row'];
type VendorRating = Database['public']['Tables']['vendor_ratings']['Row'];
type VendorServiceType = Database['public']['Enums']['vendor_service_type'];

export class VendorService {
  /**
   * Get vendor profile with all enhanced fields
   */
  static async getVendorProfile(userId: string): Promise<Vendor | null> {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching vendor profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error:', error);
      return null;
    }
  }

  /**
   * Get vendor onboarding progress from the dedicated table
   */
  static async getOnboardingProgress(vendorId: string): Promise<VendorOnboardingProgress | null> {
    try {
      const { data, error } = await supabase
        .from('vendor_onboarding_progress')
        .select('*')
        .eq('vendor_id', vendorId)
        .single();

      if (error) {
        console.error('Error fetching onboarding progress:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error:', error);
      return null;
    }
  }

  /**
   * Get all vendors with enhanced data (CupShup users only) - OPTIMIZED VERSION
   */
  static async getAllVendorsOptimized(): Promise<any[]> {
    try {
      // Get all vendors first
      const { data: vendors, error: vendorsError } = await supabase
        .from('vendors')
        .select('*')
        .order('created_at', { ascending: false });

      if (vendorsError) {
        console.error('Error fetching vendors:', vendorsError);
        return [];
      }

      if (!vendors || vendors.length === 0) {
        return [];
      }

      // Get all user IDs for batch queries
      const userIds = vendors.map(vendor => vendor.user_id);

      // Get all profiles in a single query
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, email, name')
        .in('user_id', userIds);

      // Get all onboarding progress in a single query
      const { data: progressData } = await supabase
        .from('vendor_onboarding_progress')
        .select('*')
        .in('vendor_id', userIds);

      // Get all vendor ratings in a single query
      const { data: ratingsData } = await supabase
        .from('vendor_ratings')
        .select('vendor_id, rating');

      // Create lookup maps for efficient joining
      const profilesMap = new Map();
      profiles?.forEach(profile => {
        profilesMap.set(profile.user_id, profile);
      });

      const progressMap = new Map();
      progressData?.forEach(progress => {
        progressMap.set(progress.vendor_id, progress);
      });

      // Calculate average ratings
      const ratingsMap = new Map<string, { average: number; count: number }>();
      if (ratingsData) {
        const groupedRatings = ratingsData.reduce((acc, rating) => {
          if (!acc[rating.vendor_id]) acc[rating.vendor_id] = [];
          acc[rating.vendor_id].push(rating.rating);
          return acc;
        }, {} as Record<string, number[]>);

        Object.entries(groupedRatings).forEach(([vendorId, ratings]) => {
          const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
          ratingsMap.set(vendorId, {
            average: Math.round(average * 10) / 10,
            count: ratings.length
          });
        });
      }

      // Combine all data
      const enhancedVendors = vendors.map(vendor => {
        const profile = profilesMap.get(vendor.user_id);
        const progress = progressMap.get(vendor.user_id);
        const rating = ratingsMap.get(vendor.user_id);

        return {
          ...vendor,
          email: profile?.email || '',
          onboarding_progress: progress || undefined,
          average_rating: rating?.average || 0,
          rating_count: rating?.count || 0
        };
      });

      return enhancedVendors;
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  }

  /**
   * Get all vendors with enhanced data (CupShup users only) - ORIGINAL VERSION
   */
  static async getAllVendors(): Promise<Vendor[]> {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching vendors:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  }

  /**
   * Get vendor statistics for CupShup dashboard
   */
  static async getVendorStats(): Promise<{
    total: number;
    onboardingComplete: number;
    averageCompletion: number;
    byServiceType: Record<VendorServiceType, number>;
  }> {
    try {
      const { data: progressData, error } = await supabase
        .from('vendor_onboarding_progress')
        .select('completion_percentage');

      if (error) {
        console.error('Error fetching vendor progress:', error);
        return {
          total: 0,
          onboardingComplete: 0,
          averageCompletion: 0,
          byServiceType: {} as Record<VendorServiceType, number>
        };
      }

      const total = progressData?.length || 0;
      const onboardingComplete = progressData?.filter(p => p.completion_percentage === 100).length || 0;
      const averageCompletion = total > 0 
        ? Math.round(progressData.reduce((sum, p) => sum + p.completion_percentage, 0) / total)
        : 0;

      // Get service type distribution
      const { data: vendorData } = await supabase
        .from('vendors')
        .select('service_types');

      const byServiceType: Record<VendorServiceType, number> = {
        'Manpower': 0,
        'Printing': 0,
        'Logistics': 0,
        'Goodies': 0,
        'Engagement': 0,
        'Alliance': 0,
        'Production': 0
      };

      vendorData?.forEach(vendor => {
        vendor.service_types?.forEach((type: VendorServiceType) => {
          byServiceType[type]++;
        });
      });

      return {
        total,
        onboardingComplete,
        averageCompletion,
        byServiceType
      };
    } catch (error) {
      console.error('Error getting vendor stats:', error);
      return {
        total: 0,
        onboardingComplete: 0,
        averageCompletion: 0,
        byServiceType: {} as Record<VendorServiceType, number>
      };
    }
  }

  /**
   * Search vendors by service type and other criteria
   */
  static async searchVendors(criteria: {
    serviceTypes?: VendorServiceType[];
    city?: string;
    minRating?: number;
  }): Promise<Vendor[]> {
    try {
      let query = supabase
        .from('vendors')
        .select('*');

      if (criteria.serviceTypes && criteria.serviceTypes.length > 0) {
        query = query.overlaps('service_types', criteria.serviceTypes);
      }

      if (criteria.city) {
        query = query.ilike('city', `%${criteria.city}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error searching vendors:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  }

  /**
   * Get vendor average rating
   */
  static async getVendorAverageRating(vendorId: string): Promise<{ average: number; count: number }> {
    try {
      const { data, error } = await supabase
        .from('vendor_ratings')
        .select('rating')
        .eq('vendor_id', vendorId);

      if (error || !data || data.length === 0) {
        return { average: 0, count: 0 };
      }

      const average = data.reduce((sum, rating) => sum + rating.rating, 0) / data.length;
      return { average: Math.round(average * 10) / 10, count: data.length };
    } catch (error) {
      console.error('Error:', error);
      return { average: 0, count: 0 };
    }
  }

  /**
   * Update vendor service types
   */
  static async updateServiceTypes(
    userId: string, 
    serviceTypes: VendorServiceType[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // First, update the service types
      const { error: updateError } = await supabase
        .from('vendors')
        .update({ 
          service_types: serviceTypes,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error updating service types:', updateError);
        return { success: false, error: updateError.message };
      }

      // Manually trigger onboarding progress recalculation
      // This ensures the progress is updated even if the trigger doesn't fire properly
      const { error: progressError } = await supabase.rpc('update_vendor_onboarding_progress' as any, {
        vendor_user_id: userId
      });

      if (progressError) {
        console.warn('Warning: Could not update onboarding progress:', progressError);
        // Don't fail the entire operation if progress calculation fails
      }

      return { success: true };
    } catch (error) {
      console.error('Error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  /**
   * Update vendor banking details
   */
  static async updateBankingDetails(
    userId: string, 
    bankingData: {
      beneficiary_name: string;
      account_number: string;
      ifsc_code: string;
      bank_name: string;
      upi_id?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error: updateError } = await supabase
        .from('vendors')
        .update({
          ...bankingData,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error updating banking details:', updateError);
        return { success: false, error: updateError.message };
      }

      // Manually trigger onboarding progress recalculation
      const { error: progressError } = await supabase.rpc('update_vendor_onboarding_progress' as any, {
        vendor_user_id: userId
      });

      if (progressError) {
        console.warn('Warning: Could not update onboarding progress:', progressError);
      }

      return { success: true };
    } catch (error) {
      console.error('Error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  /**
   * Upload vendor portfolio to Supabase storage
   */
  static async uploadPortfolio(
    userId: string, 
    file: File
  ): Promise<{ success: boolean; error?: string; url?: string }> {
    try {
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return { success: false, error: 'File size must be less than 10MB' };
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/gif'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        return { success: false, error: 'File type not supported. Please upload PDF, DOC, DOCX, or image files.' };
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}_${Date.now()}.${fileExt}`;
      const filePath = `portfolios/${fileName}`;

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('vendor-portfolios')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        return { success: false, error: uploadError.message };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('vendor-portfolios')
        .getPublicUrl(filePath);

      // Update vendor record with portfolio details
      const { error: updateError } = await supabase
        .from('vendors')
        .update({
          portfolio_url: publicUrl,
          portfolio_filename: file.name,
          portfolio_file_size: file.size,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error updating vendor portfolio:', updateError);
        return { success: false, error: updateError.message };
      }

      // Manually trigger onboarding progress recalculation
      const { error: progressError } = await supabase.rpc('update_vendor_onboarding_progress' as any, {
        vendor_user_id: userId
      });

      if (progressError) {
        console.warn('Warning: Could not update onboarding progress:', progressError);
      }

      return { success: true, url: publicUrl };
    } catch (error) {
      console.error('Error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  /**
   * Get vendor ratings
   */
  static async getVendorRatings(vendorId: string): Promise<VendorRating[]> {
    try {
      const { data, error } = await supabase
        .from('vendor_ratings')
        .select(`
          *,
          rated_by_profile:profiles!rated_by(name, email)
        `)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching vendor ratings:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  }

  /**
   * Rate vendor (CupShup users only)
   */
  static async rateVendor(
    vendorId: string, 
    ratedBy: string, 
    ratingData: {
      rating: number;
      comments?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('vendor_ratings')
        .upsert({
          vendor_id: vendorId,
          rated_by: ratedBy,
          rating: ratingData.rating,
          comments: ratingData.comments
        });

      if (error) {
        console.error('Error rating vendor:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }
} 